import useSWR from 'swr';
import { orgApi } from '../utils/apiV2';
import api from '../utils/api';

/**
 * Custom hooks for data fetching with SWR
 * Uses PostgreSQL v2 API where available, falls back to legacy API
 * Provides automatic caching, revalidation, and loading states
 */

// V2 API fetcher with error handling
const fetcherV2 = async (key) => {
  try {
    const endpoint = key.replace('/api/v2/', '');
    
    if (endpoint === 'roles') {
      // Use v2 roles endpoint
      try {
        const response = await orgApi.getRoles();
        return response.data?.roles || response.data || [];
      } catch {
        return [];
      }
    }
    
    if (endpoint === 'team') {
      const response = await orgApi.getTeamMembers();
      return response.data?.teamMembers || response.data || [];
    }
    
    if (endpoint === 'assets') {
      const response = await orgApi.getAssets();
      return response.data?.assets || response.data || [];
    }
    
    if (endpoint === 'services') {
      const response = await orgApi.getServices();
      return response.data?.services || response.data || [];
    }
    
    if (endpoint === 'bookings') {
      const response = await orgApi.getBookings();
      const bookings = response.data?.bookings || response.data || [];
      // Map to legacy format
      return bookings.map(b => ({
        ...b,
        preferredDate: b.bookingDate,
        preferredTime: b.startTime,
        servicePrice: b.totalPrice
      }));
    }
    
    // Fallback to legacy API
    const response = await api.get(`/${endpoint}`);
    return response.data || [];
  } catch (error) {
    console.error(`Error fetching ${key}:`, error);
    return []; // Return empty array on error to prevent render issues
  }
};

// Roles - derived from team members since there's no dedicated v2 endpoint
export const useRoles = () => {
  const { data, error, mutate } = useSWR('/api/v2/roles', fetcherV2, {
    revalidateOnFocus: true,  // Changed from false to ensure fresh data
    revalidateOnReconnect: true,
    fallbackData: [] // Prevent undefined during loading
  });
  
  return {
    roles: data || [],
    isLoading: !error && data === undefined,
    isError: !!error,
    // Force a full refetch, not just cache revalidation
    refresh: () => mutate(undefined, { revalidate: true })
  };
};

// TeamMembers/Team
export const useTeamMembers = () => {
  const { data, error, mutate } = useSWR('/api/v2/team', fetcherV2, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    fallbackData: []
  });
  
  return {
    team_members: data || [],
    isLoading: !error && data === undefined,
    isError: !!error,
    refresh: mutate
  };
};

// Assets
export const useAssets = () => {
  const { data, error, mutate } = useSWR('/api/v2/assets', fetcherV2, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    fallbackData: []
  });
  
  return {
    assets: data || [],
    isLoading: !error && data === undefined,
    isError: !!error,
    refresh: mutate
  };
};

// Services
export const useServices = () => {
  const { data, error, mutate } = useSWR('/api/v2/services', fetcherV2, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    fallbackData: []
  });
  
  return {
    services: data || [],
    isLoading: !error && data === undefined,
    isError: !!error,
    refresh: mutate
  };
};

// Schedule Bookings (calendar/schedule view)
export const useScheduleBookings = () => {
  const { data, error, mutate } = useSWR('/api/v2/bookings', fetcherV2, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    fallbackData: []
  });
  
  return {
    bookings: data || [],
    isLoading: !error && data === undefined,
    isError: !!error,
    refresh: mutate
  };
};

// Customer Bookings (from widget)
export const useBookings = () => {
  const { data, error, mutate } = useSWR('/api/v2/bookings', fetcherV2, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    fallbackData: []
  });
  
  return {
    bookings: data || [],
    isLoading: !error && data === undefined,
    isError: !!error,
    refresh: mutate
  };
};
